import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const Shop = () => {
  const { addToCart } = useCart();
  const { toast } = useToast();
  const [quantities, setQuantities] = useState({});
  const [selectedVariants, setSelectedVariants] = useState({});
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get('/api/products/');
        setProducts(response.data);
        
        // Initialize selected variants for each product
        const initialVariants = {};
        response.data.forEach(product => {
          if (product.variants && product.variants.length > 0) {
            // Find first variant with inventory
            const availableVariant = product.variants.find(v => v.inventory > 0);
            if (availableVariant) {
              initialVariants[product.id] = availableVariant.grade;
            }
          }
        });
        setSelectedVariants(initialVariants);
      } catch (error) {
        console.error('Error fetching products:', error);
        toast({
          title: "Error",
          description: "Failed to load products. Please try again later.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [toast]);

  const handleQuantityChange = (productId, value) => {
    const quantity = parseInt(value) || 1;
    setQuantities(prev => ({
      ...prev,
      [productId]: Math.max(1, quantity)
    }));
  };

  const handleVariantChange = (productId, grade) => {
    setSelectedVariants(prev => ({
      ...prev,
      [productId]: grade
    }));
  };

  const getSelectedVariantInventory = (product) => {
    if (!product.variants || product.variants.length === 0) {
      return product.inventory || 0;
    }
    const selectedGrade = selectedVariants[product.id];
    const variant = product.variants.find(v => v.grade === selectedGrade);
    return variant ? variant.inventory : 0;
  };

  const isProductAvailable = (product) => {
    if (!product.variants || product.variants.length === 0) {
      return product.inStock;
    }
    return product.variants.some(v => v.inventory > 0);
  };

  const isSelectedVariantAvailable = (product) => {
    const inventory = getSelectedVariantInventory(product);
    return inventory > 0;
  };

  const handleAddToCart = (product) => {
    trackProductView(product);
    const quantity = quantities[product.id] || 1;
    const selectedGrade = selectedVariants[product.id];
    
    addToCart({
      ...product,
      selectedVariant: selectedGrade,
      image: product.images?.[0] || product.image
    }, quantity);
    
    toast({
      title: "Added to cart!",
      description: `${quantity} x ${product.name}${selectedGrade ? ` (${selectedGrade})` : ''} added to your cart.`,
      duration: 2000,
    });
    setQuantities(prev => ({ ...prev, [product.id]: 1 }));
  };

  const trackProductView = (product) => {
    fetch('/api/analytics/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page: '/shop',
        productId: product.id,
        productName: product.name,
        sessionId: sessionStorage.getItem('maple_session_id') || ''
      })
    }).catch(() => {});
  };

  const nextImage = (productId, imagesLength) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [productId]: ((prev[productId] || 0) + 1) % imagesLength
    }));
  };

  const prevImage = (productId, imagesLength) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [productId]: ((prev[productId] || 0) - 1 + imagesLength) % imagesLength
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen py-12 flex items-center justify-center" style={{ backgroundColor: '#f7f5f2' }}>
        <p className="text-xl" style={{ color: '#736c64' }}>Loading products...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" style={{ backgroundColor: '#f7f5f2' }}>
      <div className="container mx-auto px-4">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-bold mb-4" style={{ color: '#4a2c1a', fontFamily: 'Georgia, serif' }}>
            Our Maple Syrup Collection
          </h1>
          <p className="text-xl" style={{ color: '#736c64' }}>
            Premium Grade A Pure Maple Syrup
          </p>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {products.map((product) => {
            const images = product.images && product.images.length > 0 ? product.images : [product.image];
            const currentIndex = currentImageIndex[product.id] || 0;
            const hasVariants = product.variants && product.variants.length > 0;
            const productAvailable = isProductAvailable(product);
            const selectedVariantAvailable = hasVariants ? isSelectedVariantAvailable(product) : productAvailable;
            
            return (
              <div
                key={product.id}
                className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all"
              >
                {/* Product Image Carousel */}
                <div className="relative overflow-hidden">
                  <img
                    src={images[currentIndex]}
                    alt={product.name}
                    className="w-full h-80 object-cover transition-transform"
                  />
                  
                  {/* Image Navigation */}
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={() => prevImage(product.id, images.length)}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-all"
                      >
                        <ChevronLeft className="h-5 w-5" style={{ color: '#4a2c1a' }} />
                      </button>
                      <button
                        onClick={() => nextImage(product.id, images.length)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-all"
                      >
                        <ChevronRight className="h-5 w-5" style={{ color: '#4a2c1a' }} />
                      </button>
                      
                      {/* Image Dots */}
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex space-x-2">
                        {images.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentImageIndex(prev => ({ ...prev, [product.id]: idx }))}
                            className={`w-2 h-2 rounded-full transition-all ${
                              idx === currentIndex ? 'bg-white w-4' : 'bg-white/50'
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                  
                  {/* Stock Badge */}
                  {productAvailable ? (
                    <div
                      className="absolute top-4 right-4 px-3 py-1 rounded-full flex items-center space-x-1"
                      style={{ backgroundColor: '#22c55e' }}
                    >
                      <Check className="h-4 w-4 text-white" />
                      <span className="text-sm font-semibold text-white">In Stock</span>
                    </div>
                  ) : (
                    <div
                      className="absolute top-4 right-4 px-3 py-1 rounded-full flex items-center space-x-1"
                      style={{ backgroundColor: '#dc2626' }}
                    >
                      <span className="text-sm font-semibold text-white">Out of Stock</span>
                    </div>
                  )}
                </div>

                {/* Product Details */}
                <div className="p-6">
                  <div className="mb-4">
                    <h3 className="text-2xl font-bold mb-2" style={{ color: '#4a2c1a' }}>
                      {product.name}
                    </h3>
                    <p className="text-sm mb-3" style={{ color: '#736c64' }}>
                      {product.description}
                    </p>
                    <div className="flex items-baseline space-x-2">
                      <span className="text-4xl font-bold" style={{ color: '#FF8C19' }}>
                        ${product.price}
                      </span>
                      <span className="text-sm" style={{ color: '#736c64' }}>
                        per bottle
                      </span>
                    </div>
                  </div>

                  {/* Maple Grade Variant Selector */}
                  {hasVariants && (
                    <div className="mb-4">
                      <label className="block text-sm font-semibold mb-2" style={{ color: '#4a2c1a' }}>
                        From Light to Dark (Choose Your Profile)
                      </label>
                      <Select
                        value={selectedVariants[product.id] || ''}
                        onValueChange={(value) => handleVariantChange(product.id, value)}
                      >
                        <SelectTrigger className="w-full border-2" style={{ borderColor: '#e5d5c0' }}>
                          <SelectValue placeholder="Select maple grade" />
                        </SelectTrigger>
                        <SelectContent>
                          {product.variants.map((variant) => (
                            <SelectItem 
                              key={variant.grade} 
                              value={variant.grade}
                              disabled={variant.inventory <= 0}
                            >
                              <span className="flex items-center justify-between w-full">
                                <span>{variant.grade}</span>
                                {variant.inventory <= 0 && (
                                  <span className="text-xs text-red-500 ml-2">(Out of Stock)</span>
                                )}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Quantity and Add to Cart */}
                  {selectedVariantAvailable ? (
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center border-2 rounded-lg" style={{ borderColor: '#e5d5c0' }}>
                        <button
                          className="px-3 py-2 hover:bg-gray-100 transition-colors"
                          onClick={() => handleQuantityChange(product.id, (quantities[product.id] || 1) - 1)}
                        >
                          <span className="text-xl font-bold" style={{ color: '#4a2c1a' }}>−</span>
                        </button>
                        <input
                          type="number"
                          min="1"
                          value={quantities[product.id] || 1}
                          onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                          className="w-16 text-center py-2 border-x-2 focus:outline-none"
                          style={{ borderColor: '#e5d5c0', color: '#4a2c1a' }}
                        />
                        <button
                          className="px-3 py-2 hover:bg-gray-100 transition-colors"
                          onClick={() => handleQuantityChange(product.id, (quantities[product.id] || 1) + 1)}
                        >
                          <span className="text-xl font-bold" style={{ color: '#4a2c1a' }}>+</span>
                        </button>
                      </div>
                      <Button
                        className="flex-1 text-white font-semibold py-6 hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: '#FF8C19' }}
                        onClick={() => handleAddToCart(product)}
                        disabled={hasVariants && !selectedVariants[product.id]}
                      >
                        <Plus className="h-5 w-5 mr-2" />
                        Add to Cart
                      </Button>
                    </div>
                  ) : (
                    <Button
                      disabled
                      className="w-full text-white font-semibold py-6 opacity-50 cursor-not-allowed"
                      style={{ backgroundColor: '#736c64' }}
                    >
                      Currently Unavailable
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Information Banner */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="bg-white rounded-lg p-8 shadow-md border-2" style={{ borderColor: '#FF8C19' }}>
            <h3 className="text-2xl font-bold mb-4 text-center" style={{ color: '#4a2c1a' }}>
              Why Choose The Maple Daddies?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-2" style={{ color: '#FF8C19' }}>🍁 100% Pure</h4>
                <p className="text-sm" style={{ color: '#736c64' }}>No additives or preservatives</p>
              </div>
              <div>
                <h4 className="font-semibold mb-2" style={{ color: '#FF8C19' }}>🏔️ Upstate NY</h4>
                <p className="text-sm" style={{ color: '#736c64' }}>Harvested from our own maple grove</p>
              </div>
              <div>
                <h4 className="font-semibold mb-2" style={{ color: '#FF8C19' }}>👨‍🌾 Family Made</h4>
                <p className="text-sm" style={{ color: '#736c64' }}>Small batch, handcrafted quality</p>
              </div>
              <div>
                <h4 className="font-semibold mb-2" style={{ color: '#FF8C19' }}>🚚 Fast Shipping</h4>
                <p className="text-sm" style={{ color: '#736c64' }}>Nationwide delivery in 5-7 days</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;
